import React from 'react';
import { Link } from 'react-router-dom';
import RainbowButton from '../common/rainbowButton';

interface GameAction {
  label: string;
  to: string;
  /** Static-bundle games live outside the router; use a real <a> (full page load) instead of <Link>. */
  external?: boolean;
}

interface GameCardProps {
  title: string;
  description: string;
  actions: GameAction[];
  emoji: string;
}

// Type-safe CSS custom properties
type CSSPropertiesWithVars = React.CSSProperties & {
  [key: `--${string}`]: string | number;
};

/**
 * Particles radiate outward from all 4 card edges.
 * Each has a position on an edge, a drift direction (--dx/--dy),
 * rotation (--rot), delay (--delay), and duration (--dur).
 * All animation is CSS-only via card-particle keyframes.
 * Distances are randomized: some travel ~1.5x original, others fully 2x.
 */
const PARTICLES = [
  // Top edge — drift upward (random distances: 1.3x to 2x original)
  { edge: { top: '-4px', left:  '10%' }, dx: '-18px', dy:  '-92px', rot: '-25deg', delay: '0s',    dur: '7.2s' },
  { edge: { top: '-4px', left:  '18%' }, dx: '-20px', dy: '-120px', rot: '-20deg', delay: '0.8s',  dur: '7s'   },
  { edge: { top: '-4px', left:  '35%' }, dx:  '-6px', dy:  '-85px', rot:  '-8deg', delay: '1.5s',  dur: '7.8s' },
  { edge: { top: '-4px', left:  '52%' }, dx:  '12px', dy: '-110px', rot:  '12deg', delay: '2.2s',  dur: '8s'   },
  { edge: { top: '-4px', left:  '68%' }, dx:  '14px', dy:  '-95px', rot:  '18deg', delay: '3.6s',  dur: '7.3s' },
  { edge: { top: '-4px', right: '12%' }, dx:  '24px', dy: '-130px', rot:  '28deg', delay: '4.8s',  dur: '6.5s' },
  // Bottom edge — drift downward (random distances: 1.3x to 2x original)
  { edge: { bottom: '-4px', left:  '15%' }, dx: '-15px', dy:  '88px', rot: '-22deg', delay: '0.5s',  dur: '7.8s' },
  { edge: { bottom: '-4px', left:  '30%' }, dx: '-16px', dy: '116px', rot: '-18deg', delay: '1s',    dur: '7.5s' },
  { edge: { bottom: '-4px', left:  '48%' }, dx:   '3px', dy:  '95px', rot:   '5deg', delay: '2.3s',  dur: '8.2s' },
  { edge: { bottom: '-4px', right: '22%' }, dx:  '20px', dy: '124px', rot:  '22deg', delay: '3.5s',  dur: '7s'   },
  { edge: { bottom: '-4px', right: '8%'  }, dx:  '18px', dy:  '90px', rot:  '26deg', delay: '4.2s',  dur: '7.6s' },
  // Left edge — drift left (random distances: 1.3x to 2x original)
  { edge: { top: '18%', left: '-4px' }, dx:  '-88px', dy: '-28px', rot: '-28deg', delay: '1.2s', dur: '8.3s' },
  { edge: { top: '28%', left: '-4px' }, dx: '-116px', dy: '-22px', rot: '-30deg', delay: '2.8s', dur: '8s'   },
  { edge: { top: '45%', left: '-4px' }, dx:  '-85px', dy:  '-3px', rot: '-10deg', delay: '3.8s', dur: '7.7s' },
  { edge: { top: '62%', left: '-4px' }, dx: '-104px', dy:  '16px', rot:  '15deg', delay: '5.5s', dur: '7s'   },
  { edge: { top: '78%', left: '-4px' }, dx:  '-92px', dy:  '20px', rot:  '20deg', delay: '6.8s', dur: '8.2s' },
  // Right edge — drift right (random distances: 1.3x to 2x original)
  { edge: { top: '22%', right: '-4px' }, dx:  '94px', dy: '-26px', rot: '28deg', delay: '0.7s', dur: '8.3s' },
  { edge: { top: '42%', right: '-4px' }, dx: '112px', dy: '-20px', rot: '25deg', delay: '1.5s', dur: '8.5s' },
  { edge: { top: '58%', right: '-4px' }, dx:  '86px', dy:  '-3px', rot: '12deg', delay: '2.9s', dur: '7.8s' },
  { edge: { top: '74%', right: '-4px' }, dx: '120px', dy:  '14px', rot: '30deg', delay: '4.5s', dur: '8.1s' },
];

export default function GameCard({ title, description, actions, emoji }: GameCardProps) {
  return (
    <article className="game-card">
      {/* CSS-only particles that radiate outward from card edges */}
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          className="card-particle"
          aria-hidden="true"
          style={
            {
              ...p.edge,
              '--dx': p.dx,
              '--dy': p.dy,
              '--rot': p.rot,
              '--delay': p.delay,
              '--dur': p.dur,
            } as CSSPropertiesWithVars
          }
        >
          {emoji}
        </span>
      ))}

      <h2 className="game-card__title">
        {/* Emoji stays plain white — no gradient */}
        <span className="title-emoji" aria-hidden="true">{emoji}</span>
        {/* Title text gets the vaporwave gradient */}
        <span className="title-text">{title}</span>
      </h2>
      <p className="game-card__description">{description}</p>
      <div className="game-card__actions">
        {actions.map(({ label, to, external }, i) => {
          const button = <RainbowButton className={i === 0 ? 'rainbow-button--primary' : undefined}>{label}</RainbowButton>;
          return external ? (
            <a key={to} href={to}>
              {button}
            </a>
          ) : (
            <Link key={to} to={to}>
              {button}
            </Link>
          );
        })}
      </div>
    </article>
  );
}
